import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import AppleWalletTab from './AppleWalletCard';

interface CardData {
  name: string;
  title: string;
  email: string;
  phone: string;
  website: string;
  tagline: string;
}

interface DigitalCardExportProps {
  card: CardData;
  onClose: () => void;
}

// ─── Canvas Card Renderer ─────────────────────────────────────────────────────
// Renders the branded card visual to a canvas and returns base64 PNG.
// Used for both the vCard contact photo and the "Download as PNG" feature.

async function renderCardToBase64(card: CardData, scale = 1): Promise<{ b64: string; mime: string } | null> {
  try {
    // Base dimensions (logical pixels)
    const W = 480;
    const H = Math.round(480 / 1.75); // ~274px

    const canvas = document.createElement('canvas');
    canvas.width = W * scale;
    canvas.height = H * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Scale all drawing to logical coords
    ctx.scale(scale, scale);

    // Background gradient
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#1A1A1A');
    bgGrad.addColorStop(0.5, '#2C2C2C');
    bgGrad.addColorStop(1, '#1A1A1A');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Corner accent (top-right)
    ctx.strokeStyle = 'rgba(201,169,110,0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(W - 80, 0);
    ctx.lineTo(W, 0);
    ctx.lineTo(W, 80);
    ctx.stroke();

    // Silhouette image (right side, blended)
    const isHector = card.name.toLowerCase().includes('hector');
    const imgUrl = isHector
      ? 'https://public.readdy.ai/ai/img_res/b8164775ac4a5f594477f003ad6cce2f.jpg'
      : 'https://readdy.ai/api/search-image?query=elegant%20professional%20silhouette%20in%20a%20tailored%20dark%20suit%20standing%20with%20poised%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%2C%20identity%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20presence%2C%20dark%20background%20blending%20seamlessly&width=240&height=320&seq=generic-silhouette-card-01&orientation=portrait';

    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = imgUrl;
      });
      ctx.save();
      ctx.globalAlpha = 0.55;
      const imgX = Math.round(W * 0.58);
      ctx.drawImage(img, imgX, 0, W - imgX, H);
      ctx.restore();

      // Fade silhouette to left
      const fadeGrad = ctx.createLinearGradient(Math.round(W * 0.58), 0, W, 0);
      fadeGrad.addColorStop(0, '#1A1A1A');
      fadeGrad.addColorStop(0.5, 'rgba(26,26,26,0)');
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(Math.round(W * 0.58), 0, W - Math.round(W * 0.58), H);
    } catch {
      // silhouette failed — continue without it
    }

    // Gold accent line at top
    const accentGrad = ctx.createLinearGradient(0, 0, W * 0.65, 0);
    accentGrad.addColorStop(0, 'rgba(201,169,110,0)');
    accentGrad.addColorStop(0.3, 'rgba(201,169,110,0.8)');
    accentGrad.addColorStop(0.7, 'rgba(201,169,110,0.5)');
    accentGrad.addColorStop(1, 'rgba(201,169,110,0)');
    ctx.fillStyle = accentGrad;
    ctx.fillRect(0, 0, W * 0.65, 2);

    // Gold accent line at bottom
    const bottomAccent = ctx.createLinearGradient(0, 0, W * 0.65, 0);
    bottomAccent.addColorStop(0, 'rgba(201,169,110,0.4)');
    bottomAccent.addColorStop(1, 'rgba(201,169,110,0)');
    ctx.fillStyle = bottomAccent;
    ctx.fillRect(0, H - 1, W * 0.65, 1);

    // ── HSG Logo (top-left) ──
    const pad = 22;
    ctx.font = '600 22px "Cormorant Garamond", Georgia, serif';
    ctx.fillStyle = '#C9A96E';
    ctx.letterSpacing = '3px';
    ctx.fillText('HSG', pad, pad + 16);

    const monoW = ctx.measureText('HSG').width + 6;
    ctx.strokeStyle = 'rgba(201,169,110,0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad + monoW + 10, pad + 2);
    ctx.lineTo(pad + monoW + 10, pad + 20);
    ctx.stroke();

    ctx.font = '500 7px Inter, Arial, sans-serif';
    ctx.fillStyle = '#C9A96E';
    ctx.letterSpacing = '2px';
    ctx.fillText('HIGHER STANDARD', pad + monoW + 18, pad + 10);
    ctx.font = '300 7px Inter, Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.letterSpacing = '3px';
    ctx.fillText('GLOBAL', pad + monoW + 18, pad + 22);

    // ── Name + Title (bottom-left) ──
    ctx.letterSpacing = '0px';
    const nameY = H - 60;

    const divGrad = ctx.createLinearGradient(pad, 0, pad + 160, 0);
    divGrad.addColorStop(0, 'rgba(201,169,110,0.5)');
    divGrad.addColorStop(1, 'rgba(201,169,110,0)');
    ctx.fillStyle = divGrad;
    ctx.fillRect(pad, nameY - 12, 160, 1);

    ctx.font = '500 20px "Cormorant Garamond", Georgia, serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(card.name || 'Your Name', pad, nameY + 4);

    ctx.font = '400 8px Inter, Arial, sans-serif';
    ctx.fillStyle = '#C9A96E';
    ctx.letterSpacing = '1.5px';
    ctx.fillText((card.title || '').toUpperCase(), pad, nameY + 20);

    ctx.letterSpacing = '0px';
    ctx.font = '400 7px Inter, Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    const contactY = H - 18;
    const parts = [card.email, card.phone, card.website].filter(Boolean);
    ctx.fillText(parts.join('  ·  '), pad, contactY);

    // ── Logo watermark (bottom-right) ──
    ctx.save();
    ctx.globalAlpha = 0.2;
    ctx.font = '600 13px "Cormorant Garamond", Georgia, serif';
    ctx.fillStyle = '#C9A96E';
    ctx.letterSpacing = '2px';
    ctx.fillText('HSG', W - 72, H - 14);
    ctx.restore();

    const dataUrl = canvas.toDataURL('image/png');
    const b64 = dataUrl.split(',')[1] ?? '';
    return { b64, mime: 'image/png' };
  } catch {
    return null;
  }
}

// High-res PNG data URL for download (3× scale = 1440×822px)
async function renderCardToPNG(card: CardData): Promise<string | null> {
  const result = await renderCardToBase64(card, 3);
  if (!result) return null;
  return `data:${result.mime};base64,${result.b64}`;
}

// ─── vCard Builder ────────────────────────────────────────────────────────────

function buildVCard(card: CardData, photoB64?: string, photoMime?: string): string {
  const nameParts = card.name.trim().split(' ');
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0] || '';

  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${lastName};${firstName};;;`,
    `FN:${card.name}`,
    card.title ? `TITLE:${card.title}` : '',
    'ORG:Higher Standard Global',
    card.email ? `EMAIL;TYPE=WORK,INTERNET:${card.email}` : '',
    card.phone ? `TEL;TYPE=WORK,VOICE:${card.phone}` : '',
    card.website ? `URL:https://${card.website.replace(/^https?:\/\//, '')}` : '',
    `NOTE:${card.tagline || 'By Reference Only'} — Higher Standard Global`,
  ].filter(Boolean) as string[];

  if (photoB64 && photoMime) {
    const typeStr = photoMime.split('/')[1]?.toUpperCase() ?? 'PNG';
    const header = `PHOTO;ENCODING=b;TYPE=${typeStr}:`;
    const chunkSize = 75 - header.length;
    const firstChunk = photoB64.slice(0, chunkSize);
    const rest = photoB64.slice(chunkSize);
    lines.push(header + firstChunk);
    for (let i = 0; i < rest.length; i += 74) {
      lines.push(' ' + rest.slice(i, i + 74));
    }
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
}

// ─── NFC Writer ───────────────────────────────────────────────────────────────

type NFCStatus = 'idle' | 'scanning' | 'success' | 'error' | 'unsupported';

async function writeNFC(vcard: string): Promise<void> {
  const nfcAny = (window as unknown as { NDEFReader?: unknown }).NDEFReader;
  if (!nfcAny) throw new Error('unsupported');
  const NDEFReaderClass = nfcAny as new () => {
    write: (records: { records: { recordType: string; data: string; mediaType?: string }[] }) => Promise<void>;
  };
  const ndef = new NDEFReaderClass();
  await ndef.write({
    records: [{ recordType: 'mime', mediaType: 'text/vcard', data: vcard }],
  });
}

// ─── Branded Digital Card Visual ─────────────────────────────────────────────

function BrandedCardVisual({ card }: { card: CardData }) {
  const isHector = card.name.toLowerCase().includes('hector');

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2C2C2C 50%, #1A1A1A 100%)',
        borderRadius: 16,
        aspectRatio: '1.75 / 1',
        minHeight: 180,
      }}
    >
      {/* Animated gold shimmer line */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ height: 2, zIndex: 10 }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '60%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(201,169,110,0.9) 40%, rgba(255,220,140,1) 50%, rgba(201,169,110,0.9) 60%, transparent 100%)',
            animation: 'shimmerSlide 3s ease-in-out infinite',
          }}
        />
      </div>

      {/* Pulsing gold border */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 16,
          border: '1px solid rgba(201,169,110,0.15)',
          animation: 'borderPulse 4s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />

      {/* Bottom accent line */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '65%',
          height: 1,
          background: 'linear-gradient(to right, rgba(201,169,110,0.4) 0%, rgba(201,169,110,0) 100%)',
          zIndex: 5,
        }}
      />

      {/* Corner accents */}
      <div className="absolute top-0 right-0" style={{ width: 80, height: 80, borderBottom: '1px solid rgba(201,169,110,0.25)', borderLeft: '1px solid rgba(201,169,110,0.25)', borderBottomLeftRadius: 60 }} />
      <div className="absolute bottom-0 left-0" style={{ width: 60, height: 60, borderTop: '1px solid rgba(201,169,110,0.15)', borderRight: '1px solid rgba(201,169,110,0.15)', borderTopRightRadius: 40 }} />

      {/* Silhouette portrait */}
      <div className="absolute right-0 top-0 bottom-0" style={{ width: '42%', overflow: 'hidden' }}>
        <img
          src={
            isHector
              ? 'https://readdy.ai/api/search-image?query=elegant%20male%20silhouette%20in%20a%20perfectly%20tailored%20dark%20charcoal%20suit%20standing%20with%20confident%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%20tracing%20the%20shoulder%20and%20jaw%20line%2C%20identity%20completely%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20and%20powerful%20presence%2C%20dark%20background%20blending%20seamlessly&width=240&height=320&seq=hector-silhouette-card-01&orientation=portrait'
              : 'https://readdy.ai/api/search-image?query=elegant%20professional%20silhouette%20in%20a%20tailored%20dark%20suit%20standing%20with%20poised%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%2C%20identity%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20presence%2C%20dark%20background%20blending%20seamlessly&width=240&height=320&seq=generic-silhouette-card-01&orientation=portrait'
          }
          alt=""
          className="w-full h-full object-cover object-top"
          style={{ opacity: 0.7, mixBlendMode: 'luminosity' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #1A1A1A 0%, transparent 60%)' }} />
        {/* HSG watermark */}
        <div className="absolute bottom-4 right-4 flex items-center gap-1.5" style={{ opacity: 0.35 }}>
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 14, fontWeight: 600, letterSpacing: '0.15em', color: '#C9A96E', lineHeight: 1 }}>HSG</span>
          <div style={{ width: 1, height: 14, backgroundColor: 'rgba(201,169,110,0.5)' }} />
          <div className="flex flex-col gap-0">
            <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 5, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#C9A96E', lineHeight: 1 }}>Higher Standard</span>
            <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 5, fontWeight: 300, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Global</span>
          </div>
        </div>
      </div>

      {/* Card content — left side */}
      <div className="absolute inset-0 flex flex-col justify-between p-5" style={{ width: '65%' }}>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 16, fontWeight: 600, letterSpacing: '0.15em', color: '#C9A96E', lineHeight: 1 }}>HSG</span>
          <div style={{ width: 1, height: 16, backgroundColor: 'rgba(201,169,110,0.3)' }} />
          <div className="flex flex-col gap-0.5">
            <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 6, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#C9A96E', lineHeight: 1 }}>Higher Standard</span>
            <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 6, fontWeight: 300, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Global</span>
          </div>
        </div>
        <div>
          <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 18, fontWeight: 500, color: '#FFFFFF', letterSpacing: '0.04em', lineHeight: 1.2, margin: 0, marginBottom: 4 }}>
            {card.name || 'Your Name'}
          </p>
          <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 7, fontWeight: 400, color: '#C9A96E', letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0, marginBottom: 10 }}>
            {card.title}
          </p>
          <div style={{ height: 1, backgroundColor: 'rgba(201,169,110,0.25)', marginBottom: 8 }} />
          <div className="flex flex-col gap-1">
            {card.email && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em', margin: 0 }}>{card.email}</p>}
            <div className="flex gap-3">
              {card.phone && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em', margin: 0 }}>{card.phone}</p>}
              {card.website && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em', margin: 0 }}>{card.website}</p>}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmerSlide {
          0% { left: -60%; }
          60% { left: 110%; }
          100% { left: 110%; }
        }
        @keyframes borderPulse {
          0%, 100% { border-color: rgba(201,169,110,0.12); }
          50% { border-color: rgba(201,169,110,0.35); }
        }
      `}</style>
    </div>
  );
}

// ─── PNG Export Tab ───────────────────────────────────────────────────────────

function PNGTab({ card, pngDataUrl, pngLoading }: { card: CardData; pngDataUrl: string; pngLoading: boolean }) {
  const [shared, setShared] = useState(false);

  const handleDownload = () => {
    if (!pngDataUrl) return;
    const a = document.createElement('a');
    const safeName = card.name.replace(/\s+/g, '_') || 'card';
    a.href = pngDataUrl;
    a.download = `${safeName}_HSG_Card.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    if (!pngDataUrl) return;
    try {
      const res = await fetch(pngDataUrl);
      const blob = await res.blob();
      const safeName = card.name.replace(/\s+/g, '_') || 'card';
      const file = new File([blob], `${safeName}_HSG_Card.png`, { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: `${card.name} — Higher Standard Global`, files: [file] });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      } else {
        handleDownload();
      }
    } catch {
      handleDownload();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-[#6B6B6B] leading-relaxed">
        Download the card as a high-resolution PNG — perfect for email signatures, LinkedIn banners, or sharing on social media.
      </p>

      {/* Preview */}
      <div className="rounded-xl overflow-hidden border border-[#E8E6E3]" style={{ aspectRatio: '1.75 / 1' }}>
        {pngLoading ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-[#1A1A1A]">
            <div className="w-8 h-8 border-2 border-[#C9A96E]/30 border-t-[#C9A96E] rounded-full animate-spin" />
            <p className="text-[10px] text-[#C9A96E]/60 tracking-widest uppercase">Rendering card…</p>
          </div>
        ) : pngDataUrl ? (
          <img src={pngDataUrl} alt="Card preview" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]">
            <p className="text-xs text-white/30">Preview unavailable</p>
          </div>
        )}
      </div>

      {/* Specs */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Resolution', value: '1440 × 822' },
          { label: 'Format', value: 'PNG' },
          { label: 'Scale', value: '3× Retina' },
        ].map((spec) => (
          <div key={spec.label} className="bg-[#F7F4EF] rounded-lg p-2.5 text-center">
            <p className="text-[9px] uppercase tracking-[0.15em] text-[#9A9A9A] mb-1">{spec.label}</p>
            <p className="text-xs font-medium text-[#1A1A1A]">{spec.value}</p>
          </div>
        ))}
      </div>

      {/* Use cases */}
      <div className="bg-[#F7F4EF] rounded-xl p-4 space-y-2.5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-1">Perfect for</p>
        {[
          { icon: 'ri-mail-line', text: 'Email signature — attach below your sign-off' },
          { icon: 'ri-linkedin-box-line', text: 'LinkedIn featured section or banner' },
          { icon: 'ri-instagram-line', text: 'Instagram / social media story or post' },
          { icon: 'ri-printer-line', text: 'Print-ready for physical handouts' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-[#C9A96E] flex-shrink-0"><i className={`${item.icon} text-sm`}></i></div>
            <p className="text-xs text-[#2C2C2C]">{item.text}</p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <button
          onClick={handleDownload}
          disabled={pngLoading || !pngDataUrl}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {pngLoading
            ? <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />Rendering…</>
            : <><i className="ri-download-line text-sm"></i>Download PNG</>
          }
        </button>
        <button
          onClick={handleShare}
          disabled={pngLoading || !pngDataUrl}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${shared ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30' : 'bg-white text-[#2C2C2C] border-[#E8E6E3] hover:border-[#2C2C2C]'}`}
        >
          <i className={`${shared ? 'ri-check-line' : 'ri-share-line'} text-sm`}></i>
          {shared ? 'Shared!' : 'Share Image (AirDrop / iMessage)'}
        </button>
      </div>
    </div>
  );
}

// ─── NFC Tab ──────────────────────────────────────────────────────────────────

function NFCTab({ vcard, card }: { vcard: string; card: CardData }) {
  const [nfcStatus, setNfcStatus] = useState<NFCStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const nfcSupported = !!(window as unknown as { NDEFReader?: unknown }).NDEFReader;

  const handleStartNFC = async () => {
    setNfcStatus('scanning');
    setErrorMsg('');
    try {
      await writeNFC(vcard);
      setNfcStatus('success');
      setTimeout(() => setNfcStatus('idle'), 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'unsupported') setNfcStatus('unsupported');
      else if (msg.toLowerCase().includes('abort') || msg.toLowerCase().includes('cancel')) setNfcStatus('idle');
      else { setNfcStatus('error'); setErrorMsg(msg); }
    }
  };

  const handleCancel = () => { abortRef.current?.abort(); setNfcStatus('idle'); };

  return (
    <div className="flex flex-col">
      <p className="text-xs text-[#6B6B6B] mb-5 leading-relaxed">
        Tap two iPhones together (or Android with NFC) to instantly transfer this contact — no app needed.
      </p>
      <div className="bg-[#F7F4EF] rounded-xl p-4 mb-5 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-2">How tap-to-share works</p>
        {[
          { icon: 'ri-smartphone-line', text: 'On iPhone: Use AirDrop (tap "Share Contact" below) — iPhones share contacts via AirDrop proximity tap' },
          { icon: 'ri-wifi-line', text: 'On Android: Tap "Write to NFC Tag" to write this card to any NFC tag or Android device' },
          { icon: 'ri-user-add-line', text: 'Recipient taps the notification to instantly save to Contacts' },
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center text-[#C9A96E] flex-shrink-0 mt-0.5"><i className={`${step.icon} text-sm`}></i></div>
            <p className="text-xs text-[#2C2C2C] leading-relaxed">{step.text}</p>
          </div>
        ))}
      </div>
      <div className="mb-3 p-4 rounded-xl border border-[#E8E6E3] bg-white">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-apple-line text-sm text-[#1A1A1A]"></i></div>
          <p className="text-xs font-semibold text-[#1A1A1A]">iPhone — AirDrop Tap</p>
        </div>
        <p className="text-[11px] text-[#6B6B6B] mb-3 leading-relaxed">Bring two iPhones close together, then tap &ldquo;Share Contact&rdquo; — iOS will offer AirDrop automatically.</p>
        <button
          onClick={async () => {
            const blob = new Blob([vcard], { type: 'text/vcard' });
            const file = new File([blob], `${card.name.replace(/\s+/g, '_')}_HSG.vcf`, { type: 'text/vcard' });
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
              try { await navigator.share({ title: `${card.name} — Higher Standard Global`, files: [file] }); } catch { /* cancelled */ }
            } else if (navigator.share) {
              try { await navigator.share({ title: `${card.name} — Higher Standard Global`, text: `${card.name}\n${card.title}\n${card.email}\n${card.phone}\n${card.website}` }); } catch { /* cancelled */ }
            } else {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `${card.name.replace(/\s+/g, '_')}_HSG.vcf`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-line text-sm"></i>Share Contact (AirDrop / iMessage)
        </button>
      </div>
      <div className="p-4 rounded-xl border border-[#E8E6E3] bg-white">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-android-line text-sm text-[#1A1A1A]"></i></div>
          <p className="text-xs font-semibold text-[#1A1A1A]">Android — NFC Write</p>
          {!nfcSupported && <span className="ml-auto text-[10px] text-[#9A9A9A] bg-[#F7F4EF] px-2 py-0.5 rounded-full">Requires Android Chrome</span>}
        </div>
        <p className="text-[11px] text-[#6B6B6B] mb-3 leading-relaxed">Write this contact to an NFC tag or tap to an Android device with NFC enabled.</p>
        {nfcStatus === 'scanning' && (
          <div className="flex flex-col items-center py-4 gap-3">
            <div className="relative w-14 h-14">
              <div className="absolute inset-0 rounded-full border-2 border-[#C9A96E]/30 animate-ping" />
              <div className="absolute inset-2 rounded-full border-2 border-[#C9A96E]/50 animate-ping" style={{ animationDelay: '0.3s' }} />
              <div className="absolute inset-0 flex items-center justify-center"><i className="ri-wifi-line text-[#C9A96E] text-xl"></i></div>
            </div>
            <p className="text-xs text-[#6B6B6B] text-center">Hold near NFC tag or device...</p>
            <button onClick={handleCancel} className="text-[11px] text-[#9A9A9A] hover:text-[#2C2C2C] transition-colors cursor-pointer">Cancel</button>
          </div>
        )}
        {nfcStatus === 'success' && (
          <div className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#4A7C59]/10 text-[#4A7C59]">
            <i className="ri-check-line text-sm"></i><p className="text-xs font-medium">Written successfully!</p>
          </div>
        )}
        {nfcStatus === 'error' && (
          <div className="flex flex-col gap-2 py-2">
            <div className="flex items-center gap-2 text-red-500"><i className="ri-error-warning-line text-sm"></i><p className="text-xs">NFC write failed. {errorMsg}</p></div>
            <button onClick={() => setNfcStatus('idle')} className="text-[11px] text-[#9A9A9A] hover:text-[#2C2C2C] transition-colors cursor-pointer">Try again</button>
          </div>
        )}
        {(nfcStatus === 'idle' || nfcStatus === 'unsupported') && (
          <button
            onClick={nfcSupported ? handleStartNFC : undefined}
            disabled={!nfcSupported}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${nfcSupported ? 'border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white' : 'border-[#E8E6E3] text-[#C8C5C0] cursor-not-allowed'}`}
          >
            <i className="ri-wifi-line text-sm"></i>
            {nfcSupported ? 'Write to NFC Tag' : 'NFC not available on this device'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Slug resolver ────────────────────────────────────────────────────────────

const SLUG_MAP: Record<string, string> = { hector: 'hector' };

function getCardSlug(name: string): string | null {
  const lower = name.trim().toLowerCase();
  for (const key of Object.keys(SLUG_MAP)) {
    if (lower.includes(key)) return SLUG_MAP[key];
  }
  return null;
}

// ─── iMessage helper ──────────────────────────────────────────────────────────

function openIMessage(card: CardData, cardUrl: string) {
  const msg = `Hey! Here's my digital business card — ${card.name}, ${card.title} at Higher Standard Global. Save my contact in one tap: ${cardUrl}`;
  const encoded = encodeURIComponent(msg);
  // sms: URI opens iMessage on iOS, Messages on macOS
  window.open(`sms:&body=${encoded}`, '_blank');
}

// ─── Share Link Tab ───────────────────────────────────────────────────────────

function ShareLinkTab({ card }: { card: CardData }) {
  const slug = getCardSlug(card.name);
  const cardUrl = slug ? `https://higherstandardglobal.com/card/${slug}` : null;
  const [copiedLink, setCopiedLink] = useState(false);
  const [iMessageSent, setIMessageSent] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (!cardUrl) return;
    QRCode.toDataURL(cardUrl, { width: 240, margin: 2, color: { dark: '#1A1A1A', light: '#FFFFFF' }, errorCorrectionLevel: 'M' }).then(setQrDataUrl).catch(() => {});
  }, [cardUrl]);

  const handleCopy = async () => {
    if (!cardUrl) return;
    try { await navigator.clipboard.writeText(cardUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2500); } catch { /* ignore */ }
  };

  const handleNativeShare = async () => {
    if (!cardUrl) return;
    if (navigator.share) {
      try { await navigator.share({ title: `${card.name} — Higher Standard Global`, text: `${card.name}, ${card.title} at Higher Standard Global`, url: cardUrl }); } catch { /* cancelled */ }
    } else { handleCopy(); }
  };

  const handleIMessage = () => {
    if (!cardUrl) return;
    openIMessage(card, cardUrl);
    setIMessageSent(true);
    setTimeout(() => setIMessageSent(false), 3000);
  };

  if (!cardUrl) {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F7F4EF] text-[#C9A96E]"><i className="ri-link text-xl"></i></div>
        <p className="text-xs text-[#9A9A9A] text-center leading-relaxed">A shareable link is only available for saved team members. This card doesn&apos;t have a public link yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-[#6B6B6B] leading-relaxed">Send this link to anyone — they can open it on their phone and save the contact in one tap. No app needed.</p>

      {/* Card URL display */}
      <div className="flex items-center gap-2 bg-[#F7F4EF] rounded-xl px-4 py-3 border border-[#E8E6E3]">
        <i className="ri-link text-sm text-[#C9A96E] flex-shrink-0"></i>
        <p className="text-xs text-[#2C2C2C] flex-1 truncate font-mono">{cardUrl}</p>
      </div>

      {/* ── iMessage CTA — primary action ── */}
      <button
        onClick={handleIMessage}
        className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${iMessageSent ? 'bg-[#4A7C59] text-white' : 'text-white hover:opacity-90'}`}
        style={iMessageSent ? {} : {
          background: 'linear-gradient(135deg, #34C759 0%, #28A745 100%)',
          boxShadow: '0 4px 16px rgba(52,199,89,0.35)',
        }}
      >
        {iMessageSent ? (
          <><i className="ri-check-line text-base"></i>iMessage Opened!</>
        ) : (
          <>
            <i className="ri-message-2-fill text-base"></i>
            Send via iMessage
          </>
        )}
      </button>

      {/* iMessage explainer */}
      <div className="flex items-start gap-2 bg-[#34C759]/8 rounded-lg px-3 py-2.5 border border-[#34C759]/20">
        <i className="ri-information-line text-xs text-[#28A745] flex-shrink-0 mt-0.5"></i>
        <p className="text-[10px] text-[#4B4B4B] leading-relaxed">
          Opens iMessage with a pre-filled message and your card link — just pick a contact and hit send. Works on iPhone and Mac.
        </p>
      </div>

      {/* QR code */}
      {qrDataUrl && (
        <div className="flex flex-col items-center gap-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] self-start">QR Code for this link</p>
          <div className="bg-white rounded-xl p-3 border border-[#E8E6E3]"><img src={qrDataUrl} alt="Card link QR" className="w-[160px] h-[160px]" /></div>
          <p className="text-[10px] text-[#9A9A9A] text-center">Scan to open the card page on any phone</p>
        </div>
      )}

      {/* Secondary share options */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={handleNativeShare} className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-share-line text-sm"></i>Share Link
        </button>
        <button onClick={handleCopy} className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${copiedLink ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30' : 'bg-white text-[#2C2C2C] border-[#E8E6E3] hover:border-[#2C2C2C]'}`}>
          <i className={`${copiedLink ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
          {copiedLink ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      <p className="text-[10px] text-[#9A9A9A] text-center leading-relaxed">Anyone with this link can view the card and save the contact — no login required.</p>
    </div>
  );
}

// ─── Main Export Modal ────────────────────────────────────────────────────────

export default function DigitalCardExport({ card, onClose }: DigitalCardExportProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'card' | 'wallet' | 'png' | 'qr' | 'vcf' | 'nfc' | 'link'>('card');
  const [photoB64, setPhotoB64] = useState<string>('');
  const [photoMime, setPhotoMime] = useState<string>('');
  const [photoLoading, setPhotoLoading] = useState(true);
  const [pngDataUrl, setPngDataUrl] = useState<string>('');
  const [pngLoading, setPngLoading] = useState(true);

  // Fetch the silhouette photo directly for the vCard contact photo
  useEffect(() => {
    setPhotoLoading(true);
    const isHector = card.name.toLowerCase().includes('hector');
    const silhouetteUrl = isHector
      ? 'https://readdy.ai/api/search-image?query=elegant%20male%20silhouette%20in%20a%20perfectly%20tailored%20dark%20charcoal%20suit%20standing%20with%20confident%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%20tracing%20the%20shoulder%20and%20jaw%20line%2C%20identity%20completely%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20and%20powerful%20presence%2C%20dark%20background%20blending%20seamlessly&width=480&height=640&seq=hector-silhouette-card-01&orientation=portrait'
      : 'https://readdy.ai/api/search-image?query=elegant%20professional%20silhouette%20in%20a%20tailored%20dark%20suit%20standing%20with%20poised%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%2C%20identity%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20presence%2C%20dark%20background%20blending%20seamlessly&width=480&height=640&seq=generic-silhouette-card-01&orientation=portrait';

    fetch(silhouetteUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const mime = blob.type || 'image/jpeg';
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          const b64 = result.split(',')[1] ?? '';
          setPhotoB64(b64);
          setPhotoMime(mime);
          setPhotoLoading(false);
        };
        reader.onerror = () => setPhotoLoading(false);
        reader.readAsDataURL(blob);
      })
      .catch(() => setPhotoLoading(false));
  }, [card.name]);

  // Render 3× card for PNG download
  useEffect(() => {
    setPngLoading(true);
    renderCardToPNG(card).then((url) => {
      if (url) setPngDataUrl(url);
      setPngLoading(false);
    });
  }, [card.name]);

  const vcard = buildVCard(card, photoB64 || undefined, photoMime || undefined);

  useEffect(() => {
    QRCode.toDataURL(vcard, { width: 280, margin: 2, color: { dark: '#1A1A1A', light: '#FFFFFF' }, errorCorrectionLevel: 'M' }).then(setQrDataUrl).catch(() => {});
  }, [vcard]);

  const handleDownloadVCF = () => {
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = card.name.replace(/\s+/g, '_') || 'contact';
    a.href = url; a.download = `${safeName}_HSG.vcf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    const safeName = card.name.replace(/\s+/g, '_') || 'contact';
    a.href = qrDataUrl; a.download = `${safeName}_HSG_QR.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleDownloadPNG = () => {
    if (!pngDataUrl) return;
    const a = document.createElement('a');
    const safeName = card.name.replace(/\s+/g, '_') || 'card';
    a.href = pngDataUrl; a.download = `${safeName}_HSG_Card.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleCopyVCard = async () => {
    try { await navigator.clipboard.writeText(vcard); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* ignore */ }
  };

  const tabs = [
    { id: 'card' as const, label: 'Card', icon: 'ri-id-card-line' },
    { id: 'wallet' as const, label: 'Wallet', icon: 'ri-apple-line' },
    { id: 'png' as const, label: 'PNG', icon: 'ri-image-line' },
    { id: 'link' as const, label: 'Link', icon: 'ri-link' },
    { id: 'qr' as const, label: 'QR', icon: 'ri-qr-code-line' },
    { id: 'vcf' as const, label: 'Contact', icon: 'ri-contacts-line' },
    { id: 'nfc' as const, label: 'Tap', icon: 'ri-wifi-line' },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1A1A1A] px-6 pt-6 pb-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96E] mb-1">Digital Business Card</p>
              <h2 className="text-xl font-medium text-white leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                {card.name || 'Contact'}
              </h2>
              <p className="text-xs text-white/40 mt-0.5">{card.title}</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors cursor-pointer flex-shrink-0 mt-0.5">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          {/* Tabs — 7 tabs in rows */}
          <div className="grid grid-cols-4 gap-1 bg-white/10 rounded-lg p-1">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-md text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.id ? 'bg-[#C9A96E] text-[#1A1A1A]' : 'text-white/50 hover:text-white/80'}`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                <span className="leading-none">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">

          {/* Digital Card Tab */}
          {activeTab === 'card' && (
            <div className="flex flex-col gap-5">
              <BrandedCardVisual card={card} />
              <div className="bg-[#F7F4EF] rounded-xl p-4 space-y-2.5">
                {[
                  { icon: 'ri-user-line', value: card.name },
                  { icon: 'ri-briefcase-line', value: card.title },
                  { icon: 'ri-building-line', value: 'Higher Standard Global' },
                  { icon: 'ri-mail-line', value: card.email },
                  { icon: 'ri-phone-line', value: card.phone },
                  { icon: 'ri-global-line', value: card.website },
                ].filter(r => r.value).map((row, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center text-[#C9A96E] flex-shrink-0"><i className={`${row.icon} text-sm`}></i></div>
                    <p className="text-xs text-[#2C2C2C] truncate">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={handleDownloadVCF} disabled={photoLoading}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {photoLoading
                    ? <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />Preparing...</>
                    : <><i className="ri-user-add-line text-sm"></i>Save Contact</>
                  }
                </button>
                <button onClick={handleDownloadPNG} disabled={pngLoading}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-[#C9A96E] text-[#C9A96E] text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {pngLoading
                    ? <><div className="w-3 h-3 border border-[#C9A96E]/40 border-t-[#C9A96E] rounded-full animate-spin" />Rendering...</>
                    : <><i className="ri-download-2-line text-sm"></i>Download PNG</>
                  }
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setActiveTab('wallet')}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap border border-[#C9A96E]/30"
                >
                  <i className="ri-apple-line text-sm"></i>Wallet
                </button>
                <button
                  onClick={() => {
                    const slug = getCardSlug(card.name);
                    const url = slug ? `https://higherstandardglobal.com/card/${slug}` : 'https://higherstandardglobal.com';
                    openIMessage(card, url);
                  }}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #34C759 0%, #28A745 100%)' }}
                >
                  <i className="ri-message-2-fill text-sm"></i>iMessage
                </button>
                <button onClick={() => setActiveTab('nfc')}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-[#2C2C2C] text-[#2C2C2C] text-xs font-medium hover:bg-[#2C2C2C] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-wifi-line text-sm"></i>Tap
                </button>
              </div>
              <div className="flex items-center gap-2 bg-[#F7F4EF] rounded-lg px-3 py-2">
                <i className="ri-image-line text-xs text-[#C9A96E] flex-shrink-0"></i>
                <p className="text-[10px] text-[#8B7355] leading-relaxed">
                  {photoLoading ? 'Loading silhouette photo for contact…' : 'Silhouette portrait embedded as contact photo in the .vcf'}
                </p>
              </div>
            </div>
          )}

          {/* Apple Wallet Tab */}
          {activeTab === 'wallet' && <AppleWalletTab card={card} />}

          {/* PNG Export Tab */}
          {activeTab === 'png' && (
            <PNGTab card={card} pngDataUrl={pngDataUrl} pngLoading={pngLoading} />
          )}

          {/* QR Tab */}
          {activeTab === 'qr' && (
            <div className="flex flex-col items-center">
              <p className="text-xs text-[#6B6B6B] text-center mb-5 leading-relaxed">Point an iPhone camera at this QR code — it will instantly offer to save the contact.</p>
              <div className="bg-white rounded-xl p-4 border border-[#E8E6E3] mb-5">
                {qrDataUrl ? <img src={qrDataUrl} alt="QR Code" className="w-[200px] h-[200px]" /> : (
                  <div className="w-[200px] h-[200px] flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="w-full space-y-2">
                <button onClick={handleDownloadQR} disabled={!qrDataUrl}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  <i className="ri-download-line text-sm"></i>Download QR Image
                </button>
                <p className="text-[10px] text-[#9A9A9A] text-center">Save the QR image to share via iMessage, email, or print on materials.</p>
              </div>
            </div>
          )}

          {/* VCF Tab */}
          {activeTab === 'vcf' && (
            <div className="flex flex-col">
              <p className="text-xs text-[#6B6B6B] mb-5 leading-relaxed">
                Download the <strong className="text-[#1A1A1A]">.vcf contact file</strong> and AirDrop it to an iPhone, or email it — tapping the file adds the contact instantly.
              </p>
              <div className="bg-[#F7F4EF] rounded-xl p-4 mb-5 space-y-2.5">
                {[
                  { icon: 'ri-user-line', value: card.name },
                  { icon: 'ri-briefcase-line', value: card.title },
                  { icon: 'ri-building-line', value: 'Higher Standard Global' },
                  { icon: 'ri-mail-line', value: card.email },
                  { icon: 'ri-phone-line', value: card.phone },
                  { icon: 'ri-global-line', value: card.website },
                ].filter(r => r.value).map((row, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center text-[#C9A96E] flex-shrink-0"><i className={`${row.icon} text-sm`}></i></div>
                    <p className="text-xs text-[#2C2C2C] truncate">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <button onClick={handleDownloadVCF} disabled={photoLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {photoLoading
                    ? <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />Rendering card…</>
                    : <><i className="ri-download-line text-sm"></i>Download .vcf File</>
                  }
                </button>
                <button onClick={handleCopyVCard}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${copied ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30' : 'bg-white text-[#2C2C2C] border-[#E8E6E3] hover:border-[#2C2C2C]'}`}
                >
                  <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                  {copied ? 'Copied to clipboard!' : 'Copy vCard text'}
                </button>
              </div>
            </div>
          )}

          {/* Share Link Tab */}
          {activeTab === 'link' && <ShareLinkTab card={card} />}

          {/* NFC / Tap & Share Tab */}
          {activeTab === 'nfc' && <NFCTab vcard={vcard} card={card} />}
        </div>
      </div>
    </div>
  );
}
